import { workspacesCacheOwner } from './workspaces-cache';

/**
 * Which dashboards exist (and their names), kept in this browser per signed-in account, so a reload starts the
 * dashboard iframe on the first frame instead of after the existence probe — a full GET of the render document,
 * which can take seconds. The probe still runs at once and has the last word: a dashboard it finds gone is
 * forgotten here and the screen switches to its empty state.
 *
 * Only dashboards that EXIST are saved. A saved "empty" would paint the empty state on a reload and then flip to
 * the dashboard when one had been built since, which is worse than the loader it replaces.
 *
 * Same owner rule as the Spaces list (`lib/workspaces-cache.ts`): an entry answers only for the account of this page
 * load, so another account's dashboards never start loading. Cleared on sign-out. Storage is optional: every access
 * is wrapped, and a refused, full or corrupt store reads as a miss.
 */

const STORAGE_KEY = 'symphony.dashboard-meta.v1';
const MAX_AGE_MS = 14 * 24 * 60 * 60_000;
const MAX_ENTRIES = 64;
const MAX_BYTES = 50_000;

export interface SavedDashboardMeta {
  name: string;
  slug: string;
}

interface Snapshot {
  schema: 1;
  owner: string;
  entries: Record<string, SavedDashboardMeta & { savedAt: number }>;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readSnapshot(owner: string): Snapshot['entries'] {
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (!raw || raw.length > MAX_BYTES) return {};
    const value = JSON.parse(raw) as Partial<Snapshot> | null;
    if (!value || value.schema !== 1 || value.owner !== owner || !value.entries || typeof value.entries !== 'object') return {};
    return value.entries;
  } catch {
    return {};
  }
}

function writeSnapshot(owner: string, entries: Snapshot['entries']): void {
  try {
    const raw = JSON.stringify({ schema: 1, owner, entries } satisfies Snapshot);
    if (raw.length > MAX_BYTES) return;
    storage()?.setItem(STORAGE_KEY, raw);
  } catch {
    /* private mode / quota: the probe still serves this load */
  }
}

/** This account's saved meta for a dashboard key, or undefined: none saved, another account's, too old, or malformed. */
export function readSavedDashboardMeta(key: string): SavedDashboardMeta | undefined {
  const owner = workspacesCacheOwner();
  if (!owner) return undefined;
  const entry = readSnapshot(owner)[key];
  if (!entry || typeof entry !== 'object') return undefined;
  if (typeof entry.name !== 'string' || typeof entry.slug !== 'string' || !entry.slug) return undefined;
  const age = typeof entry.savedAt === 'number' ? Date.now() - entry.savedAt : Infinity;
  if (!(age >= 0 && age <= MAX_AGE_MS)) return undefined;
  return { name: entry.name, slug: entry.slug };
}

/** Saves a dashboard the probe just found, or forgets it (`null`) when the probe found nothing there. */
export function writeSavedDashboardMeta(key: string, meta: SavedDashboardMeta | null): void {
  const owner = workspacesCacheOwner();
  if (!owner) return;
  const entries = readSnapshot(owner);
  if (meta === null) {
    if (!(key in entries)) return;
    delete entries[key];
  } else {
    const prev = entries[key];
    if (prev && prev.name === meta.name && prev.slug === meta.slug && Date.now() - prev.savedAt < 60 * 60_000) return;
    delete entries[key];
    entries[key] = { name: meta.name, slug: meta.slug, savedAt: Date.now() };
    // Insertion order is recency (the entry just written is last), so the oldest go first.
    const keys = Object.keys(entries);
    for (let i = 0; i < keys.length - MAX_ENTRIES; i++) delete entries[keys[i]];
  }
  writeSnapshot(owner, entries);
}

/** Forgets every saved dashboard (sign-out). */
export function clearSavedDashboardMeta(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to forget where nothing could be written */
  }
}
